import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Send, Eye, FlaskConical, CheckCircle2, XCircle, RefreshCw, Megaphone, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AUDIENCES = [
  ["appway", "AppWay", "Пользователи ботов AppWay"],
  ["prosto", "ProstoApp", "Пользователи бота ProstoApp"],
  ["both", "Оба бота", "Обе аудитории, без повторной отправки одному человеку"],
];

const STATUS_LABEL = {
  draft: ["Черновик", "bg-slate-500/15 text-slate-300"],
  sending: ["Идёт рассылка", "bg-blue-500/15 text-blue-300"],
  paused: ["Пауза", "bg-amber-500/15 text-amber-300"],
  done: ["Завершена", "bg-emerald-500/15 text-emerald-300"],
  canceled: ["Отменена", "bg-red-500/15 text-red-300"],
  failed: ["Ошибка", "bg-red-500/15 text-red-300"],
};

function Stat({ label, value, cls = "text-slate-200" }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 py-2 text-center">
      <div className={`font-display font-bold ${cls}`}>{value}</div>
      <div className="text-[10px] text-slate-500">{label}</div>
    </div>
  );
}

function Progress({ campaign, onDone }) {
  const [camp, setCamp] = useState(campaign);
  const timer = useRef(null);

  const poll = useCallback(async () => {
    try {
      const d = await api.bcGet(campaign.id);
      setCamp(d);
      if (["done", "canceled", "failed"].includes(d.status)) {
        clearInterval(timer.current);
        onDone && onDone();
      }
    } catch {
      /* ignore transient */
    }
  }, [campaign.id, onDone]);

  useEffect(() => {
    poll();
    timer.current = setInterval(poll, 2000);
    return () => clearInterval(timer.current);
  }, [poll]);

  const c = camp.counts || {};
  const [sl, sc] = STATUS_LABEL[camp.status] || [camp.status, "bg-white/5 text-slate-300"];
  const processed = (c.sent || 0) + (c.blocked || 0) + (c.unreachable || 0) + (c.failed || 0);
  const pct = camp.total ? Math.round((processed / camp.total) * 100) : 0;

  const cancel = async () => {
    try {
      await api.bcCancel(camp.id);
      toast.success("Рассылка остановлена");
      poll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось остановить");
    }
  };

  return (
    <div data-testid="broadcast-progress" className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${sc}`}>{sl}</span>
        <span className="text-xs text-slate-500">Всего: {camp.total}</span>
        {camp.status === "sending" && (
          <button data-testid="broadcast-cancel" onClick={cancel} className="ml-auto text-xs px-3 py-1.5 rounded-full bg-red-500/15 text-red-300">
            Остановить
          </button>
        )}
      </div>
      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Отправлено" value={c.sent || 0} cls="text-emerald-300" />
        <Stat label="Заблокир." value={c.blocked || 0} cls="text-orange-300" />
        <Stat label="Недоступно" value={c.unreachable || 0} cls="text-slate-400" />
        <Stat label="Ошибки" value={c.failed || 0} cls="text-red-300" />
        <Stat label="В очереди" value={(c.queued || 0) + (c.temp_error || 0)} cls="text-blue-300" />
        <Stat label="Прогресс" value={`${pct}%`} />
      </div>
    </div>
  );
}

export function AdminBroadcast() {
  const [audience, setAudience] = useState("appway");
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [testId, setTestId] = useState("");
  const [busy, setBusy] = useState("");
  const [campaign, setCampaign] = useState(null);
  const [confirmArmed, setConfirmArmed] = useState(false);
  const [history, setHistory] = useState([]);

  const loadPreview = useCallback(async (a) => {
    try {
      setPreview(await api.bcPreview(a));
    } catch {
      setPreview(null);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await api.bcList());
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    api.bcDefaultText().then((d) => setText(d.text)).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    loadPreview(audience);
    setConfirmArmed(false);
  }, [audience, loadPreview]);

  const sendTest = async () => {
    const id = Number(testId);
    if (!id) return toast.error("Укажите ваш Telegram ID");
    setBusy("test");
    try {
      await api.bcTest({ audience, text, chat_id: id });
      toast.success("Тестовое сообщение отправлено вам");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось отправить тест");
    } finally {
      setBusy("");
    }
  };

  const createAndConfirm = async () => {
    if (!confirmArmed) {
      setConfirmArmed(true);
      return;
    }
    setBusy("send");
    try {
      const camp = await api.bcCreate({ audience, text });
      await api.bcConfirm(camp.id);
      setCampaign(camp);
      setConfirmArmed(false);
      toast.success("Рассылка запущена");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось запустить рассылку");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-broadcast" className="space-y-4">
      <div className="flex items-center gap-2 text-slate-300">
        <Megaphone size={18} className="text-blue-400" />
        <span className="font-display font-bold">Рассылка пользователям</span>
      </div>

      {/* Аудитория */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-400 font-bold mb-2">Аудитория</div>
        <div className="grid grid-cols-1 gap-2">
          {AUDIENCES.map(([k, l, d]) => (
            <button
              key={k}
              data-testid={`broadcast-audience-${k}`}
              onClick={() => setAudience(k)}
              className={`text-left px-4 py-3 rounded-2xl border transition ${audience === k ? "bg-blue-500/15 border-blue-500/40" : "bg-white/[0.03] border-white/10"}`}
            >
              <div className="text-sm font-semibold">{l}</div>
              <div className="text-xs text-slate-500">{d}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Получатели */}
      {preview && (
        <div data-testid="broadcast-preview" className="grid grid-cols-3 gap-2">
          <Stat label="Получателей" value={preview.total} cls="text-emerald-300" />
          <Stat label="Отписались" value={preview.excluded_optout} cls="text-slate-400" />
          <Stat label="AppWay / Prosto" value={`${preview.by_channel?.appway ?? 0}/${preview.by_channel?.prosto ?? 0}`} />
        </div>
      )}

      {/* Текст */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-400 font-bold mb-1">Текст сообщения</div>
        <textarea
          data-testid="broadcast-text"
          value={text}
          onChange={(e) => { setText(e.target.value); setConfirmArmed(false); }}
          rows={6}
          className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-blue-500/50 text-sm"
        />
        <div className="text-[11px] text-slate-500 mt-1">К сообщению автоматически добавится кнопка «Открыть».</div>
      </div>

      {/* Предпросмотр сообщения */}
      <div data-testid="broadcast-message-preview" className="rounded-2xl bg-white/[0.03] border border-white/10 p-3">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-2"><Eye size={13} /> Как увидит пользователь</div>
        <div className="rounded-xl bg-[#1b2735] px-3 py-2 text-sm whitespace-pre-wrap break-words">{text || "—"}</div>
        <div className="mt-2 text-center text-xs font-semibold text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-xl py-2">
          {audience === "prosto" ? "⚡ Открыть ProstoApp" : "🚀 Открыть AppWay"}
        </div>
      </div>

      {/* Предупреждение */}
      <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
        <span>Часть сообщений может не дойти: пользователь остановил бота или бот не может ему написать. Повторно одному человеку в рамках кампании сообщение не отправляется.</span>
      </div>

      {/* Тест себе */}
      <div className="flex gap-2">
        <input
          data-testid="broadcast-test-id"
          value={testId}
          onChange={(e) => setTestId(e.target.value.replace(/[^0-9]/g, ""))}
          placeholder="Ваш Telegram ID для теста"
          className="flex-1 min-w-0 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none"
        />
        <button
          data-testid="broadcast-test-send"
          onClick={sendTest}
          disabled={busy === "test"}
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-slate-200 disabled:opacity-60"
        >
          {busy === "test" ? <Loader2 size={15} className="animate-spin" /> : <FlaskConical size={15} />} Тест себе
        </button>
      </div>

      {/* Подтверждение / запуск */}
      {confirmArmed && (
        <div data-testid="broadcast-confirm-warn" className="text-sm text-center text-slate-300 bg-blue-500/10 border border-blue-500/30 rounded-xl py-2 px-3">
          Отправить <b>{preview?.total ?? "?"}</b> получателям? Нажмите ещё раз для подтверждения.
        </div>
      )}
      <div className="flex gap-2">
        <button
          data-testid="broadcast-send"
          onClick={createAndConfirm}
          disabled={busy === "send" || !text.trim() || !(preview?.total)}
          className={`flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-2xl font-bold active:scale-95 transition disabled:opacity-50 ${confirmArmed ? "bg-gradient-to-r from-emerald-600 to-emerald-500" : "bg-gradient-to-r from-blue-600 to-blue-500 glow-blue"}`}
        >
          {busy === "send" ? <Loader2 size={18} className="animate-spin" /> : confirmArmed ? <CheckCircle2 size={18} /> : <Send size={18} />}
          {confirmArmed ? "Подтвердить рассылку" : "Отправить рассылку"}
        </button>
        {confirmArmed && (
          <button data-testid="broadcast-cancel-arm" onClick={() => setConfirmArmed(false)} className="px-4 rounded-2xl bg-white/5 border border-white/10 text-slate-300">
            <XCircle size={18} />
          </button>
        )}
      </div>

      {campaign && <Progress campaign={campaign} onDone={loadHistory} />}

      {/* История кампаний */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="text-xs uppercase tracking-wide text-slate-400 font-bold">История рассылок</div>
          <button data-testid="broadcast-history-refresh" onClick={loadHistory} className="text-slate-400"><RefreshCw size={14} /></button>
        </div>
        <div className="space-y-2">
          {history.map((h) => {
            const [sl, sc] = STATUS_LABEL[h.status] || [h.status, "bg-white/5 text-slate-300"];
            return (
              <button
                key={h.id}
                data-testid={`broadcast-history-${h.id}`}
                onClick={() => setCampaign(h)}
                className="w-full text-left p-3 rounded-2xl bg-white/[0.03] border border-white/10"
              >
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span>{new Date(h.created_at).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                  <span>·</span>
                  <span>{h.audience === "both" ? "Оба бота" : h.audience === "prosto" ? "ProstoApp" : "AppWay"}</span>
                  <span className={`ml-auto px-2 py-0.5 rounded-full font-medium ${sc}`}>{sl}</span>
                </div>
                <div className="mt-1 text-xs text-slate-400">
                  {(h.counts?.sent || 0)}/{h.total} отправлено
                </div>
              </button>
            );
          })}
          {!history.length && <div className="py-6 text-center text-sm text-slate-500" data-testid="broadcast-history-empty">Рассылок пока не было</div>}
        </div>
      </div>
    </div>
  );
}
